import * as dayjs from 'dayjs';

export const STATUS_OK = 200;
export const STATUS_NODATA = 300;   // pseudo-status: sensor is monitoring but no data came back yet
export const STATUS_ERROR = 400;
export const STATUS_STOPPED = 0;    // sensor not monitoring

export type StatusKind = 'ok' | 'error' | 'stopped' | 'nodata' | 'mixed';

export const STATUS_LABEL: Record<StatusKind, string> = {
    ok: 'OK',
    error: 'Error',
    stopped: 'Stopped',
    nodata: 'No Data',
    mixed: 'Mixed',
};

// The backend aggregates finer-grained samples into each timeslice with
// sqrt(avg(status²)), so a slice that was partly OK and partly Error comes
// back as a value between the two codes. Only the exact codes are pure — any
// other value means the slice itself was a blend.
export const statusKind = (s: number): StatusKind => {
    switch (s) {
        case STATUS_OK: return 'ok';
        case STATUS_ERROR: return 'error';
        case STATUS_NODATA: return 'nodata';
        case STATUS_STOPPED: return 'stopped';
        default: return 'mixed';
    }
};

export interface BucketSummary {
    kind: StatusKind;
    counts: Partial<Record<StatusKind, number>>;
}

// Summarize statuses[from..to) (to exclusive): the bucket's kind is the single
// kind all slices share, or 'mixed' when kinds differ (or any slice is itself
// an RMS blend). Counts record how many slices of each kind the bucket holds.
export const summarizeBucket = (statuses: number[], from: number, to: number): BucketSummary => {
    const counts: Partial<Record<StatusKind, number>> = {};
    let kind: StatusKind | null = null;
    for (let i = from; i < to; i++) {
        const k = statusKind(statuses[i]);
        counts[k] = (counts[k] ?? 0) + 1;
        kind = kind === null || kind === k ? k : 'mixed';
    }
    return { kind: kind ?? 'nodata', counts };
};

// Compact duration: "45s", "5m", "1m 30s", "1h 5m".
export const formatSeconds = (secs: number): string => {
    const whole = Math.round(secs);
    const h = Math.floor(whole / 3600);
    const m = Math.floor((whole % 3600) / 60);
    const s = whole % 60;
    const parts: string[] = [];
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    if (s || parts.length === 0) parts.push(`${s}s`);
    return parts.join(' ');
};

// Tie-break order for equal durations — worst news first.
const KIND_SEVERITY: Record<StatusKind, number> = { error: 5, mixed: 4, stopped: 3, ok: 2, nodata: 1 };

// How much of an RMS-blend slice was in error, assuming it blends OK and
// Error samples: v = sqrt(p·400² + (1−p)·200²) inverts to the share below.
// Blends involving No Data skew the estimate, hence the "≈" marker.
const blendErrorShare = (v: number): number =>
    Math.min(1, Math.max(0, (v * v - STATUS_OK * STATUS_OK) /
        (STATUS_ERROR * STATUS_ERROR - STATUS_OK * STATUS_OK)));

// Per-kind durations for statuses[from..to), longest first: "OK 10m, Error 5m".
// Pure slices contribute their full interval to their kind; RMS-blend slices
// are split into estimated Error/OK time, and the result is prefixed with "≈"
// to flag that it contains estimates.
export const estimatedBreakdown = (statuses: number[], from: number, to: number, intervalSeconds: number): string => {
    const seconds: Partial<Record<StatusKind, number>> = {};
    const add = (k: StatusKind, secs: number) => { if (secs > 0) seconds[k] = (seconds[k] ?? 0) + secs; };
    let approx = false;
    for (let i = from; i < to; i++) {
        const kind = statusKind(statuses[i]);
        if (kind === 'mixed') {
            approx = true;
            const p = blendErrorShare(statuses[i]);
            add('error', p * intervalSeconds);
            add('ok', (1 - p) * intervalSeconds);
        } else {
            add(kind, intervalSeconds);
        }
    }
    // Round to whole seconds before sorting so float noise from the share
    // estimate can't defeat the severity tie-break.
    const text = (Object.entries(seconds) as [StatusKind, number][])
        .map(([k, secs]) => [k, Math.round(secs)] as [StatusKind, number])
        .sort((a, b) => b[1] - a[1] || KIND_SEVERITY[b[0]] - KIND_SEVERITY[a[0]])
        .map(([k, secs]) => `${STATUS_LABEL[k]} ${formatSeconds(secs)}`)
        .join(', ');
    return approx ? `≈ ${text}` : text;
};

export interface CellLayout {
    cellCount: number;
    slicesPerBucket: number;
}

// One cell per timeslice, so the display granularity always matches the
// selected interval (like the activity charts). The cap is only a DOM-safety
// valve for pathological URL-crafted range/interval combos — beyond it slices
// are merged uniformly (every bucket spans the same number of slices; the
// last may be shorter when the total doesn't divide evenly).
export const computeCellLayout = (totalSlices: number, cap: number): CellLayout => {
    if (totalSlices === 0) return { cellCount: 0, slicesPerBucket: 1 };
    const maxCells = Math.max(1, Math.min(totalSlices, cap));
    const slicesPerBucket = Math.ceil(totalSlices / maxCells);
    return { cellCount: Math.ceil(totalSlices / slicesPerBucket), slicesPerBucket };
};

// Timeslice index range [start, end) covered by bucket b.
export const bucketBounds = (b: number, slicesPerBucket: number, totalSlices: number) => ({
    start: b * slicesPerBucket,
    end: Math.min((b + 1) * slicesPerBucket, totalSlices),
});

// Timeslices mark the START of each period, so the range ends one interval
// after the last slice begins — that's the timestamp tooltips should show as
// the final period's end.
export const rangeEndTimestamp = (timeslices: string[], intervalSeconds: number): string => {
    if (timeslices.length === 0) return '';
    return dayjs.default(timeslices[timeslices.length - 1])
        .add(intervalSeconds, 'second')
        .format('YYYY-MM-DD HH:mm:ss');
};
